import { DEG, clamp } from '../lib/math';

// How bright the sky is overhead, and across the sky, and so the faintest star the eye can see. Plain sums with no
// page attached, so the events worker can use them as well as the sky.

const BORTLE_SQM = [22.0, 21.9, 21.6, 21.1, 20.4, 19.5, 18.9, 18.4, 17.8];

const PARANAL_NIGHT = 10 ** (-0.4 * 21.7); // dark-time zenith V at Paranal (Patat 2003)
/**
 * Twilight alone at the zenith, V mag/arcsec², for the Sun `d` degrees below the horizon. From 5° to 15°:
 * Patat, Ugolnikov & Postylyakov (2006, A&A 455, 385, Table 1, V: 11.84 + 1.518x − 0.057x², x = d − 5),
 * less Paranal's own night sky; beyond, it keeps fading at the same rate; above 5°, it eases to sunset.
 */
function twilightSky(d: number): number {
  if (d <= 5) return 11 + 0.168 * d;
  if (d > 15) return 22.66 + 1.2 * (d - 15);
  const x = d - 5;
  return -2.5 * Math.log10(10 ** (-0.4 * (11.84 + 1.518 * x - 0.057 * x * x)) - PARANAL_NIGHT);
}

/** The light pollution overhead (mag/arcsec², the natural sky included): measured or estimated, else the Bortle class's. */
const pollutedSky = (bortle: number, sqm?: number | null) => sqm ?? BORTLE_SQM[clamp(Math.round(bortle), 1, 9) - 1];
/** Moonlight overhead (mag/arcsec²) with the Moon `moonAlt` degrees up; 99 (none) while it is down. */
const moonSky = (moonAlt: number, moonMag: number) =>
  moonAlt > 0 ? 18.9 + (moonMag + 12.7) - 2.5 * Math.log10(Math.max(Math.sin(moonAlt * DEG), 0.08)) : 99;

/** Twilight (or daylight) `sTw` with the light pollution and the Moon added: the zenith's brightness, mag/arcsec². */
function withLightAndMoon(sTw: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): number {
  const flux = Math.pow(10, -0.4 * pollutedSky(bortle, sqm)) + Math.pow(10, -0.4 * sTw) + Math.pow(10, -0.4 * moonSky(moonAlt, moonMag));
  return -2.5 * Math.log10(flux);
}

/** Zenith sky brightness (mag/arcsec²) from light pollution, twilight and moonlight. */
export function skyBrightness(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): number {
  return withLightAndMoon(sunAlt > 0 ? 11 - 0.4 * sunAlt : twilightSky(-sunAlt), moonAlt, moonMag, bortle, sqm);
}

/**
 * The same for the eye (Naked eye and the darkness check), with twilight as bright as it is: from 5° below the horizon
 * as above, but brighter by 0.87 magnitudes a degree to about 7.5 at sunset (some 100 cd/m²) and on into daylight,
 * where twilightSky eases to only 11 (about 4 cd/m², far darker than the sky then is). Full sky keeps skyBrightness.
 */
export function eyeSkyBrightness(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): number {
  return withLightAndMoon(eyeTwilight(sunAlt), moonAlt, moonMag, bortle, sqm);
}
/** Twilight (or daylight) overhead as eyeSkyBrightness has it (mag/arcsec²). */
function eyeTwilight(sunAlt: number): number {
  const d = -sunAlt;
  return d >= 5 ? twilightSky(d) : Math.max(3, 11.84 - 0.87 * (5 - d));
}

/** The natural night sky overhead with no light pollution (V mag/arcsec²): Bortle class 1's, near the darkest
 *  Krisciunas & Schaefer (1991) give for a clear site, 21.8 to 22.0. */
export const NATURAL_SKY = BORTLE_SQM[0];
/** The light overhead by its sources, each as a flux (10^(−0.4 S)). */
export interface SkyParts { nat: number; art: number; tw: number; moon: number }
/**
 * eyeSkyBrightness's light by its sources: the natural sky (up to NATURAL_SKY), artificial light (the rest of the light
 * pollution figure), twilight and the Moon. They add up to eyeSkyBrightness; across the sky each glows its own way.
 */
export function eyeSkyParts(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): SkyParts {
  const lp = Math.pow(10, -0.4 * pollutedSky(bortle, sqm)), nat = Math.min(lp, Math.pow(10, -0.4 * NATURAL_SKY));
  return { nat, art: lp - nat, tw: Math.pow(10, -0.4 * eyeTwilight(sunAlt)), moon: moonAlt > 0 ? Math.pow(10, -0.4 * moonSky(moonAlt, moonMag)) : 0 };
}

// ---------- Across the sky ----------
// The sky is brighter towards the horizon and around the Moon than overhead, so the eye sees fewer stars there. Each
// source of the light overhead has its own shape across the sky, relative to overhead (Naked eye uses them: see
// render/nakedeye.ts, eyeLimitAt):
//  - the natural sky (airglow and faint stars): Krisciunas & Schaefer (1991, PASP 103, 1033; K&S below) eq. 2,
//    B(Z) = B_zen·10^(−0.4k(X−1))·X, their simplification of Garstang (1989, PASP 101, 306) eqs. 29 and 30, where 60% of
//    the light comes from a van Rhijn layer of airglow 130 km up;
//  - artificial light: scattered by the air over the observer, it brightens towards the horizon in every direction,
//    most of all towards towns (Garstang 1986, PASP 98, 364, section IV; Garstang 1989). Not knowing where the towns
//    lie, it is taken as lighting the air evenly, so it grows with the column of air in view, [1 − 10^(−0.4kX)], the
//    single-scattering term of K&S eqs. 12 and 15;
//  - moonlight: K&S eq. 15, f(ρ)·[1 − 10^(−0.4kX)], with their scattering function f (moonScatter); the Moon's own
//    dimming by the air is the same in every direction, so only f and the column change;
//  - twilight: as overhead, everywhere.
// X is K&S's scattering air mass (eq. 3), at most 5 on the horizon; stars are dimmed by the extinction air mass (eq. 14).

/** K&S 1991 eq. 3: the scattering air mass where the zenith distance's cosine is `c` (1 overhead, 5 on the horizon). */
export const glowAirMass = (c: number) => 1 / Math.sqrt(0.04 + 0.96 * c * c);
/** The natural sky at scattering air mass `X`, relative to overhead (K&S 1991 eq. 2), for extinction `k` (mag per air mass). */
export const naturalGlow = (X: number, k: number) => X * Math.pow(10, -0.4 * k * (X - 1));
/** Light scattered from the column of air in view at scattering air mass `X`, relative to overhead (K&S 1991 eqs. 12, 15). */
export const columnGlow = (X: number, k: number) => (1 - Math.pow(10, -0.4 * k * X)) / (1 - Math.pow(10, -0.4 * k));
/** K&S 1991's Rayleigh constant C_R: 2.27 × 10⁵ fitted (eq. 17), 10^5.36 in their final formula (eq. 21). */
export const KS_RAYLEIGH = Math.pow(10, 5.36);
/**
 * K&S 1991's scattering function f(ρ), for the angle ρ from the Moon in degrees: Rayleigh scattering by the air,
 * C_R·(1.06 + cos²ρ) (eqs. 17, 21), and Mie scattering by aerosols, 10^(6.15 − ρ/40) (eq. 18), which within 10° gives
 * way to the aureole, 6.2 × 10⁷ ρ⁻² (eq. 19, after Schaefer 1991). The two Mie forms cross at 8.8°; within 10° the
 * larger is taken, so the glow has no step there.
 */
export function moonScatter(rho: number): number {
  const c = Math.cos(rho * DEG);
  const mie = Math.pow(10, 6.15 - rho / 40);
  return KS_RAYLEIGH * (1.06 + c * c) + (rho < 10 ? Math.max(mie, 6.2e7 / Math.max(rho * rho, 0.0625)) : mie);
}

/** Hecht's bright-light threshold, made larger than the laboratory's for an eye outdoors (see eyeLimitFromSky). */
const EYE_FIELD = 5;
/** eyeLimitFromSky's bright-light branch is HECHT_ZERO − 5·log10(1 + √(0.1259·B)): the star shader's form of it. */
export const HECHT_ZERO = -2.5 * Math.log10(EYE_FIELD * 4.808e-8) - 13.99;
/**
 * The faintest star the eye sees against a sky of brightness `S` (mag/arcsec²): Schaefer's night formula (as
 * limitingMagnitude), or where the sky is bright, as in twilight, Hecht's bright-light threshold (as given by Crumey
 * 2014, arXiv:1405.4209, eq. 20, with B in cd/m² and the threshold in lux), whichever sees fainter. The night formula alone has no
 * bright-light branch, so in early twilight it hid every star. EYE_FIELD (1.75 magnitudes) and the twilight slope in
 * eyeSkyBrightness are set so the limit follows the usual rules for the first stars: first magnitude about 3° below the
 * horizon, second at the end of civil twilight (6°). An estimate, for checking outdoors.
 */
export function eyeLimitFromSky(S: number): number {
  const night = 7.93 - 5 * Math.log10(10 ** (4.316 - S / 5) + 1);
  const B = 10 ** ((12.58 - S) / 2.5);
  const I = EYE_FIELD * 4.808e-8 * (1 + Math.sqrt(0.1259 * B)) ** 2;
  return Math.max(night, -2.5 * Math.log10(I) - 13.99);
}

/** The eye's limit overhead (Naked eye, the darkness check and the cards): eyeLimitFromSky of eyeSkyBrightness. */
export function eyeLimitingMagnitude(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): number {
  return eyeLimitFromSky(eyeSkyBrightness(sunAlt, moonAlt, moonMag, bortle, sqm));
}

/** Naked-eye limiting magnitude from sky brightness (Schaefer's approximation). Pass `sqm` to use a measured or estimated zenith brightness. */
export function limitingMagnitude(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm?: number | null): number {
  const S = skyBrightness(sunAlt, moonAlt, moonMag, bortle, sqm);
  return 7.93 - 5 * Math.log10(Math.pow(10, 4.316 - S / 5) + 1);
}
